import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toolbar } from 'primereact/toolbar';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './PayerProfiles.scss';

class PayerProfiles extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            profiles: [],
            professionCategories: {},
            harvestSeasons: {},
            filters: {
                profession: null,
                classId: null,
                sectionId: null
            },
            globalFilter: '',
            
            // Edit Dialog
            showEditDialog: false,
            selectedProfile: null,
            editData: {
                professionCategory: null,
                parentProfession: '',
                salaryDate: null,
                harvestSeason: null,
                isPrimaryPayer: true,
                primaryPayerName: '',
                primaryPayerRelation: ''
            },
            
            // Stats
            stats: {
                totalProfiles: 0,
                byProfession: {},
                byCashFlowPattern: {},
                withSalaryDate: 0,
                withHarvestSeason: 0,
                readyForReminder: 0,
                waitingForTiming: 0
            }
        };

        this.relationOptions = [
            { label: 'Father', value: 'FATHER' },
            { label: 'Mother', value: 'MOTHER' },
            { label: 'Guardian', value: 'GUARDIAN' },
            { label: 'Grandfather', value: 'GRANDFATHER' },
            { label: 'Grandmother', value: 'GRANDMOTHER' },
            { label: 'Uncle', value: 'UNCLE' },
            { label: 'Aunt', value: 'AUNT' },
            { label: 'Brother', value: 'BROTHER' },
            { label: 'Sister', value: 'SISTER' },
            { label: 'Other', value: 'OTHER' }
        ];
    }

    componentDidMount() {
        this.fetchProfessionCategories();
        this.fetchPayerProfiles();
        this.fetchStats();
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    fetchProfessionCategories = async () => {
        try {
            const url = `${baseUrlAdmin}/fee-decision/parent-profiles/professions`;
            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    professionCategories: response.res.data.professions || {},
                    harvestSeasons: response.res.data.harvestSeasons || {}
                });
            }
        } catch (error) {
            console.error('Error fetching profession categories:', error);
        }
    }

    fetchPayerProfiles = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');

        if (!academicYear) {
            this.showToast('error', 'Error', 'Missing academic year');
            return;
        }

        this.setState({ loading: true });

        try {
            const { filters } = this.state;
            let url = `${baseUrlAdmin}/fee-decision/parent-profiles/payers?academicYear=${academicYear}`;

            if (filters.profession) {
                url += `&profession=${filters.profession}`;
            }

            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    profiles: response.res.data || []
                });
            } else {
                this.showToast('error', 'Error', 'Failed to fetch payer profiles');
            }
        } catch (error) {
            console.error('Error fetching payer profiles:', error);
            this.showToast('error', 'Error', 'Failed to fetch payer profiles');
        } finally {
            this.setState({ loading: false });
        }
    }

    fetchStats = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');

        if (!academicYear) return;

        try {
            const url = `${baseUrlAdmin}/fee-decision/parent-profiles/stats?academicYear=${academicYear}`;
            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    stats: response.res.data || this.state.stats
                });
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }

    handleFilterChange = (field, value) => {
        this.setState(prevState => ({
            filters: {
                ...prevState.filters,
                [field]: value
            }
        }), () => {
            this.fetchPayerProfiles();
            this.fetchStats();
        });
    }

    clearFilters = () => {
        this.setState({
            filters: {
                profession: null,
                classId: null,
                sectionId: null
            },
            globalFilter: ''
        }, () => {
            this.fetchPayerProfiles();
            this.fetchStats();
        });
    }

    openEditDialog = (profile) => {
        this.setState({
            selectedProfile: profile,
            editData: {
                professionCategory: profile.professionCategory || 'OTHER',
                parentProfession: profile.parentProfession || '',
                salaryDate: profile.salaryDate || null,
                harvestSeason: profile.harvestSeason || null,
                isPrimaryPayer: profile.isPrimaryPayer !== false,
                primaryPayerName: profile.primaryPayerName || '',
                primaryPayerRelation: profile.primaryPayerRelation || ''
            },
            showEditDialog: true
        });
    }

    closeEditDialog = () => {
        this.setState({
            showEditDialog: false,
            selectedProfile: null,
            editData: {
                professionCategory: null,
                parentProfession: '',
                salaryDate: null,
                harvestSeason: null,
                isPrimaryPayer: true,
                primaryPayerName: '',
                primaryPayerRelation: ''
            }
        });
    }

    saveProfile = async () => {
        const { selectedProfile, editData } = this.state;
        const academicYear = localStorage.getItem('userAcademicYear');

        if (!selectedProfile || !academicYear) {
            this.showToast('error', 'Error', 'Missing required information');
            return;
        }

        this.setState({ loading: true });

        try {
            const url = `${baseUrlAdmin}/fee-decision/parent-profiles/${selectedProfile.user_id}?academicYear=${academicYear}`;
            
            const response = await this.service.put(url, editData, true);

            if (response?.status && response?.res?.success) {
                this.showToast('success', 'Success', 'Parent profile updated successfully');
                this.closeEditDialog();
                this.fetchPayerProfiles();
                this.fetchStats();
            } else {
                this.showToast('error', 'Error', response?.res?.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            this.showToast('error', 'Error', 'Failed to update profile');
        } finally {
            this.setState({ loading: false });
        }
    }

    exportCSV = () => {
        this.dt.exportCSV();
    }

    // Template Methods
    professionBodyTemplate = (rowData) => {
        const details = rowData.professionDetails || {};
        const severity = this.getProfessionSeverity(rowData.professionCategory);
        
        return (
            <div>
                <Tag value={details.label || rowData.professionCategory} severity={severity} />
                <div className="profession-pattern">{details.cashFlowPattern || 'UNKNOWN'}</div>
            </div>
        );
    }

    getProfessionSeverity = (category) => {
        switch (category) {
            case 'SALARIED': return 'success';
            case 'BUSINESS': return 'warning';
            case 'FARMER': return 'info';
            case 'PROFESSIONAL': return 'primary';
            case 'DAILY_WAGE': return 'danger';
            default: return 'secondary';
        }
    }

    timingBodyTemplate = (rowData) => {
        const timing = rowData.recommendedReminderTiming || {};
        const isReady = timing.timing === 'IMMEDIATE';
        
        return (
            <div className="timing-info">
                <Tag 
                    value={isReady ? 'Ready' : 'Wait'} 
                    severity={isReady ? 'success' : 'warning'} 
                    icon={isReady ? 'pi pi-check-circle' : 'pi pi-clock'}
                />
                <div className="timing-reason">{timing.reason || 'N/A'}</div>
            </div>
        );
    }

    salaryDateBodyTemplate = (rowData) => {
        if (!rowData.salaryDate) return <span className="text-muted">Not Set</span>;
        return <span className="font-bold">{rowData.salaryDate}{this.getDaySuffix(rowData.salaryDate)} of month</span>;
    }

    getDaySuffix = (day) => {
        if (day >= 11 && day <= 13) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    }

    harvestSeasonBodyTemplate = (rowData) => {
        if (!rowData.harvestSeasonDetails) return <span className="text-muted">Not Set</span>;
        return <span>{rowData.harvestSeasonDetails.label}</span>;
    }

    contactBodyTemplate = (rowData) => {
        return (
            <div className="contact-info">
                <div className="contact-primary">{rowData.parentContactNo || 'N/A'}</div>
                {rowData.alternateContactNo && (
                    <div className="contact-alternate">Alt: {rowData.alternateContactNo}</div>
                )}
            </div>
        );
    }

    actionBodyTemplate = (rowData) => {
        return (
            <Authorizer permId={PERMISSIONS.FDE_UPDATE_PAYER_PROFILE}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text"
                    onClick={() => this.openEditDialog(rowData)}
                    tooltip="Edit Profile"
                />
            </Authorizer>
        );
    }

    render() {
        const { loading, profiles, professionCategories, harvestSeasons, filters, globalFilter, 
                showEditDialog, editData, stats } = this.state;

        if (loading && profiles.length === 0) {
            return <LoadingComponent />;
        }

        const professionOptions = Object.keys(professionCategories).map(key => ({
            label: professionCategories[key].label,
            value: key
        }));

        const harvestSeasonOptions = Object.keys(harvestSeasons).map(key => ({
            label: harvestSeasons[key].label,
            value: key
        }));

        return (
            <div className="payer-profiles">
                <Toast ref={this.toast} position="bottom-right" />

                {/* Header Card with Stats */}
                <Card className="header-card">
                    <div className="header-content">
                        <div className="header-left">
                            <h2>Payer Profiles & Cash-Flow Context</h2>
                            <p className="description-text">
                                Manage parent profession, cash-flow patterns, and optimal reminder timing
                            </p>
                        </div>
                        <div className="header-stats">
                            <div className="stat-item">
                                <span className="stat-label">Total Profiles</span>
                                <span className="stat-value">{stats.totalProfiles}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Ready for Reminder</span>
                                <span className="stat-value success">{stats.readyForReminder}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Waiting for Timing</span>
                                <span className="stat-value warning">{stats.waitingForTiming}</span>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Filters & Actions Card */}
                <Card className="filters-card">
                    <Toolbar
                        left={(
                            <div className="filters-section">
                                <div className="filter-item">
                                    <label>Profession</label>
                                    <Dropdown
                                        value={filters.profession}
                                        options={professionOptions}
                                        onChange={(e) => this.handleFilterChange('profession', e.value)}
                                        placeholder="All Professions"
                                        showClear
                                        className="filter-dropdown"
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Search</label>
                                    <InputText
                                        value={globalFilter}
                                        onChange={(e) => this.setState({ globalFilter: e.target.value })}
                                        placeholder="Search..."
                                        className="filter-input"
                                    />
                                </div>

                                <Button
                                    label="Clear"
                                    icon="pi pi-filter-slash"
                                    className="p-button-outlined"
                                    onClick={this.clearFilters}
                                />
                            </div>
                        )}
                        right={(
                            <div className="actions-section">
                                <Authorizer permId={PERMISSIONS.FDE_EXPORT_PAYER_PROFILES}>
                                    <Button
                                        label="Export CSV"
                                        icon="pi pi-download"
                                        className="p-button-outlined p-button-success"
                                        onClick={this.exportCSV}
                                    />
                                </Authorizer>
                                <Button
                                    label="Refresh"
                                    icon="pi pi-refresh"
                                    className="p-button-outlined"
                                    onClick={() => {
                                        this.fetchPayerProfiles();
                                        this.fetchStats();
                                    }}
                                    loading={loading}
                                />
                            </div>
                        )}
                    />
                </Card>

                {/* Data Table Card */}
                <Card className="table-card">
                    <DataTable
                        ref={(el) => this.dt = el}
                        value={profiles}
                        paginator
                        rows={20}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        globalFilter={globalFilter}
                        emptyMessage="No payer profiles found"
                        className="profiles-table"
                        loading={loading}
                        scrollable
                    >
                        <Column 
                            field="studentName" 
                            header="Student" 
                            sortable 
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            field="admissionNo" 
                            header="Admission No" 
                            sortable 
                        />
                        <Column 
                            field="className" 
                            header="Class" 
                            sortable 
                        />
                        <Column 
                            field="parentName" 
                            header="Parent Name" 
                            sortable 
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            header="Contact" 
                            body={this.contactBodyTemplate} 
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            header="Profession" 
                            body={this.professionBodyTemplate} 
                            sortable 
                            sortField="professionCategory"
                            style={{ minWidth: '180px' }}
                        />
                        <Column 
                            header="Salary Date" 
                            body={this.salaryDateBodyTemplate} 
                            style={{ minWidth: '120px' }}
                        />
                        <Column 
                            header="Harvest Season" 
                            body={this.harvestSeasonBodyTemplate} 
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            header="Reminder Timing" 
                            body={this.timingBodyTemplate} 
                            style={{ minWidth: '180px' }}
                        />
                        <Column 
                            header="Actions" 
                            body={this.actionBodyTemplate} 
                            style={{ width: '100px' }}
                        />
                    </DataTable>
                </Card>

                {/* Edit Profile Dialog */}
                <Dialog
                    visible={showEditDialog}
                    onHide={this.closeEditDialog}
                    header="Edit Parent Profile"
                    style={{ width: '600px' }}
                    className="edit-profile-dialog"
                >
                    {loading ? (
                        <div style={{ minHeight: '400px', position: 'relative' }}>
                            <LoadingComponent />
                        </div>
                    ) : (
                        <>
                            <div className="p-fluid">
                                <div className="field">
                                    <label>Profession Category *</label>
                                    <Dropdown
                                        value={editData.professionCategory}
                                        options={professionOptions}
                                        onChange={(e) => {
                                            const category = e.value;
                                            const newData = { ...editData, professionCategory: category };
                                            
                                            // Reset specific fields based on new category to prevent data contamination
                                            if (category !== 'FARMER') {
                                                newData.harvestSeason = null;
                                            }
                                            
                                            if (category !== 'SALARIED' && category !== 'RETIRED') {
                                                newData.salaryDate = null;
                                            }
                                            
                                            this.setState({ editData: newData });
                                        }}
                                        placeholder="Select Profession"
                                    />
                                    <small className="help-text">
                                        {professionCategories[editData.professionCategory]?.description}
                                    </small>
                                </div>

                                <div className="field">
                                    <label>Profession Details</label>
                                    <InputText
                                        value={editData.parentProfession}
                                        onChange={(e) => this.setState({ editData: { ...editData, parentProfession: e.target.value } })}
                                        placeholder="e.g., Software Engineer, Farmer, Shop Owner"
                                    />
                                </div>

                                {(editData.professionCategory === 'SALARIED' || editData.professionCategory === 'RETIRED') && (
                                    <div className="field">
                                        <label>Salary/Pension Date (Day of Month)</label>
                                        <InputNumber
                                            value={editData.salaryDate}
                                            onValueChange={(e) => this.setState({ editData: { ...editData, salaryDate: e.value } })}
                                            placeholder="Enter day (1-31)"
                                            min={1}
                                            max={31}
                                        />
                                        <small className="help-text">
                                            Reminders will be sent after this date
                                        </small>
                                    </div>
                                )}

                                {editData.professionCategory === 'FARMER' && (
                                    <div className="field">
                                        <label>Harvest Season</label>
                                        <Dropdown
                                            value={editData.harvestSeason}
                                            options={harvestSeasonOptions}
                                            onChange={(e) => this.setState({ editData: { ...editData, harvestSeason: e.value } })}
                                            placeholder="Select Harvest Season"
                                        />
                                        <small className="help-text">
                                            Reminders will be aligned with harvest periods
                                        </small>
                                    </div>
                                )}

                                <div className="field">
                                    <label>Primary Payer Name</label>
                                    <InputText
                                        value={editData.primaryPayerName}
                                        onChange={(e) => this.setState({ editData: { ...editData, primaryPayerName: e.target.value } })}
                                        placeholder="Who pays the fees?"
                                    />
                                </div>

                                <div className="field">
                                    <label>Relation to Student</label>
                                    <Dropdown
                                        value={editData.primaryPayerRelation}
                                        options={this.relationOptions}
                                        onChange={(e) => this.setState({ editData: { ...editData, primaryPayerRelation: e.value } })}
                                        placeholder="Select Relation"
                                        showClear
                                    />
                                </div>

                                <div className="field-checkbox">
                                    <input
                                        type="checkbox"
                                        id="isPrimaryPayer"
                                        checked={editData.isPrimaryPayer}
                                        onChange={(e) => this.setState({ editData: { ...editData, isPrimaryPayer: e.target.checked } })}
                                    />
                                    <label htmlFor="isPrimaryPayer">This contact is the primary fee payer</label>
                                </div>
                            </div>

                            <div className="dialog-footer">
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times"
                                    className="p-button-text"
                                    onClick={this.closeEditDialog}
                                />
                                <Authorizer permId={PERMISSIONS.FDE_UPDATE_PAYER_PROFILE}>
                                    <Button
                                        label="Save"
                                        icon="pi pi-check"
                                        onClick={this.saveProfile}
                                    />
                                </Authorizer>
                            </div>
                        </>
                    )}
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    userInfo: state?.user?.userInfo || null
});

export default connect(mapStateToProps)(withRouter(PayerProfiles));
